/**
 * Product Growth Slide
 * Two interlocking feedback cycles sharing a central User Engagement node,
 * plus a Vision Guided Products curved arrow from above.
 * Node/arrow colors follow the red-orange-yellow-green-blue progression.
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import {
  SlideBase,
  SlideContent,
  SlideHeader,
  SlideAccentGlow,
  SlideAccentGlowBottom,
} from '@components/press/pitch-deck/slide-primitives';

/* -------------------------------------------------- */
/* Color palette (base accent: red-orange-yellow-green-blue) */
/* -------------------------------------------------- */

const PAL = {
  red:    { base: colors.accent.red.base,    dark: colors.accent.red.base,    gradFrom: colors.accent.red.exxtraDark,    gradTo: colors.accent.red.base },
  orange: { base: colors.accent.orange.base, dark: colors.accent.orange.base, gradFrom: colors.accent.orange.exxtraDark, gradTo: colors.accent.orange.base },
  yellow: { base: colors.accent.yellow.base, dark: colors.accent.yellow.base, gradFrom: colors.accent.yellow.exxtraDark, gradTo: colors.accent.yellow.base },
  green:  { base: colors.accent.green.base,  dark: colors.accent.green.base,  gradFrom: colors.accent.green.exxtraDark,  gradTo: colors.accent.green.base },
  blue:   { base: colors.accent.blue.base,   dark: colors.accent.blue.base,   gradFrom: colors.accent.blue.exxtraDark,   gradTo: colors.accent.blue.base },
};

// Vision Guided Products — Coral (exxtraDark-based gradient)
const VIS_COLOR = { base: colors.accent.coral.base, dark: colors.accent.coral.base, gradFrom: colors.accent.coral.exxxtraDark, gradTo: colors.accent.coral.exxtraDark };

// Gradient IDs for node fills
const GRAD_IDS = { red: 'g-red', orange: 'g-orange', yellow: 'g-yellow', green: 'g-green', blue: 'g-blue', coral: 'g-coral' };

const GRADIENTS = [
  { id: GRAD_IDS.red,    from: PAL.red.gradFrom,    to: PAL.red.gradTo },
  { id: GRAD_IDS.orange, from: PAL.orange.gradFrom, to: PAL.orange.gradTo },
  { id: GRAD_IDS.yellow, from: PAL.yellow.gradFrom, to: PAL.yellow.gradTo },
  { id: GRAD_IDS.green,  from: PAL.green.gradFrom,  to: PAL.green.gradTo },
  { id: GRAD_IDS.blue,   from: PAL.blue.gradFrom,   to: PAL.blue.gradTo },
  { id: GRAD_IDS.coral,  from: VIS_COLOR.gradFrom,  to: VIS_COLOR.gradTo },
];

/** Map a PAL entry to its gradient fill url */
function gradFill(pal: { base: string }) {
  if (pal.base === PAL.red.base) return `url(#${GRAD_IDS.red})`;
  if (pal.base === PAL.orange.base) return `url(#${GRAD_IDS.orange})`;
  if (pal.base === PAL.yellow.base) return `url(#${GRAD_IDS.yellow})`;
  if (pal.base === PAL.green.base) return `url(#${GRAD_IDS.green})`;
  if (pal.base === PAL.blue.base) return `url(#${GRAD_IDS.blue})`;
  return `url(#${GRAD_IDS.coral})`;
}

/* -------------------------------------------------- */
/* Per-node color assignments                         */
/* -------------------------------------------------- */

// Left cycle (clockwise flow from UE):
//   UE(blue) → Logs(red) → AI Bug Analysis(orange) → AI Coding Agent(yellow) → Product Improvements(green) → UE
const LEFT_NODE_COLORS  = [PAL.blue, PAL.red, PAL.orange, PAL.yellow, PAL.green];
// Arrow color = color of the node it points INTO
const LEFT_ARROW_COLORS = [PAL.red, PAL.orange, PAL.yellow, PAL.green, PAL.blue];

// Right cycle (reversed arrows — flow goes: Product Ideas→AI Coding Agent→AI Triaging→Product Feedback→UE):
//   Nodes placed CW: UE(0), Product Feedback(1), AI Triaging(2), AI Coding Agent(3), Product Ideas(4)
//   By flow distance from UE: Product Ideas=red, AI Coding Agent=orange, AI Triaging=yellow, Product Feedback=green
const RIGHT_NODE_COLORS  = [PAL.blue, PAL.green, PAL.yellow, PAL.orange, PAL.red];
// Reversed arrows go: 1→0, 2→1, 3→2, 4→3, 0→4  (color of destination node)
const RIGHT_ARROW_COLORS = [PAL.blue, PAL.green, PAL.yellow, PAL.orange, PAL.red];

/* -------------------------------------------------- */
/* Layout                                             */
/* -------------------------------------------------- */

const W = 960;
const H = 500;

const LOOP_R = 145;
const NODE_R = 43; // 20% larger than 36
const HUB_R = 53;

const UE_X = W / 2;
const UE_Y = H / 2 + 15;

const L_CX = UE_X - LOOP_R;
const L_CY = UE_Y;
const R_CX = UE_X + LOOP_R;
const R_CY = UE_Y;

const VIS_X = UE_X + 110;
const VIS_Y = 40;

/* -------------------------------------------------- */
/* Geometry helpers                                   */
/* -------------------------------------------------- */

function toRad(deg: number) {
  return ((deg - 90) * Math.PI) / 180;
}

function pos(cx: number, cy: number, r: number, deg: number) {
  const rad = toRad(deg);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcCW(cx: number, cy: number, r: number, aDeg: number, bDeg: number, gap = 20): string {
  const s = pos(cx, cy, r, aDeg + gap);
  const e = pos(cx, cy, r, bDeg - gap);
  return `M${s.x},${s.y} A${r},${r} 0 0 1 ${e.x},${e.y}`;
}

function arcCCW(cx: number, cy: number, r: number, aDeg: number, bDeg: number, gap = 20): string {
  const s = pos(cx, cy, r, aDeg - gap);
  const e = pos(cx, cy, r, bDeg + gap);
  return `M${s.x},${s.y} A${r},${r} 0 0 0 ${e.x},${e.y}`;
}

/* -------------------------------------------------- */
/* SVG multiline text                                 */
/* -------------------------------------------------- */

const SvgText: React.FC<{
  x: number; y: number; text: string;
  fill?: string; size?: number; weight?: string;
}> = ({ x, y, text, fill = colors.highlight.dark, size = 13, weight = '500' }) => {
  const lines = text.split('\n');
  const lh = size * 1.3;
  const startY = y - ((lines.length - 1) * lh) / 2;
  return (
    <text x={x} y={startY} textAnchor="middle" dominantBaseline="central"
      fill={fill} fontSize={size} fontWeight={weight} fontFamily={typography.fontFamily.body}>
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : lh}>{line}</tspan>
      ))}
    </text>
  );
};

/* -------------------------------------------------- */
/* Styled wrappers                                    */
/* -------------------------------------------------- */

const DiagramWrapper = styled.div`
  width: 100%;
  max-width: 980px;
`;

const DiagramSvg = styled.svg`
  width: 100%;
  height: auto;
  display: block;
`;

/* -------------------------------------------------- */
/* Node definitions                                   */
/* -------------------------------------------------- */

const LEFT_ANGLES = [90, 162, 234, 306, 18];
const LEFT_LABELS = ['User\nEngagement', 'Logs', 'AI Bug\nAnalysis', 'AI Coding\nAgent', 'Deploy'];

const RIGHT_ANGLES = [270, 342, 54, 126, 198];
const RIGHT_LABELS = ['User\nEngagement', 'Deploy', 'AI Coding\nAgent', 'AI\nTriaging', 'User\nIdeas'];

/* -------------------------------------------------- */
/* Arrow marker IDs — one per color                   */
/* -------------------------------------------------- */

const MARKER_COLORS = [
  { id: 'a-red',    color: PAL.red.dark },
  { id: 'a-orange', color: PAL.orange.dark },
  { id: 'a-yellow', color: PAL.yellow.dark },
  { id: 'a-green',  color: PAL.green.dark },
  { id: 'a-blue',   color: PAL.blue.dark },
  { id: 'a-coral',  color: VIS_COLOR.dark },
];

function markerId(pal: { dark: string }) {
  const entry = MARKER_COLORS.find(m => m.color === pal.dark);
  return entry ? `url(#${entry.id})` : '';
}

/* -------------------------------------------------- */
/* Component                                          */
/* -------------------------------------------------- */

export const ProductGrowthSlide: React.FC = () => {
  const leftNodes = LEFT_ANGLES.map((a, i) => ({ ...pos(L_CX, L_CY, LOOP_R, a), label: LEFT_LABELS[i] }));
  const rightNodes = RIGHT_ANGLES.map((a, i) => ({ ...pos(R_CX, R_CY, LOOP_R, a), label: RIGHT_LABELS[i] }));

  return (
    <SlideBase>
      <SlideAccentGlow />
      <SlideAccentGlowBottom />
      <SlideContent>
        <SlideHeader
          title="Product Growth"
          subtitle="AI Near Instant Reactions Quickly Capitalize on Market Pull"
        />

        <DiagramWrapper>
          <DiagramSvg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
            <defs>
              {MARKER_COLORS.map(m => (
                <marker key={m.id} id={m.id} viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="6" markerHeight="4" orient="auto">
                  <path d="M0,1 L10,5 L0,9 Z" fill={m.color} />
                </marker>
              ))}
              {GRADIENTS.map(g => (
                <linearGradient key={g.id} id={g.id} x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor={g.from} />
                  <stop offset="100%" stopColor={g.to} />
                </linearGradient>
              ))}
            </defs>

            {/* ═══════════ LEFT CYCLE ═══════════ */}

            <circle cx={L_CX} cy={L_CY} r={LOOP_R}
              fill={colors.brand.primaryDark} opacity={0.4} />
            <circle cx={L_CX} cy={L_CY} r={LOOP_R}
              fill="none" stroke={colors.brand.primaryDark} strokeWidth={1.5} strokeDasharray="8 5" opacity={0.4} />

            <SvgText x={L_CX} y={L_CY} text={"Bug & Performance\nFlywheel"}
              fill={colors.highlight.primary} size={16} weight="500" />

            {/* Clockwise arrows — each colored by destination node */}
            {LEFT_ANGLES.map((a, i) => {
              const b = LEFT_ANGLES[(i + 1) % LEFT_ANGLES.length];
              const ac = LEFT_ARROW_COLORS[i];
              return (
                <path key={`la${i}`} d={arcCW(L_CX, L_CY, LOOP_R, a, b)}
                  fill="none" stroke={ac.dark} strokeWidth={4} markerEnd={markerId(ac)} />
              );
            })}

            {/* Nodes (skip idx 0 = UE) */}
            {leftNodes.slice(1).map((n, i) => {
              const nc = LEFT_NODE_COLORS[i + 1];
              return (
                <g key={`ln${i}`}>
                  <circle cx={n.x} cy={n.y} r={NODE_R} fill={gradFill(nc)} stroke="white" strokeWidth={2} />
                  <SvgText x={n.x} y={n.y} text={n.label} size={14} weight="500" fill="white" />
                </g>
              );
            })}

            {/* ═══════════ RIGHT CYCLE (reversed arrows) ═══════════ */}

            <circle cx={R_CX} cy={R_CY} r={LOOP_R}
              fill={colors.brand.primaryDark} opacity={0.4} />
            <circle cx={R_CX} cy={R_CY} r={LOOP_R}
              fill="none" stroke={colors.brand.primaryDark} strokeWidth={1.5} strokeDasharray="8 5" opacity={0.4} />

            <SvgText x={R_CX} y={R_CY} text={"User Ideas\nFlywheel"}
              fill={colors.highlight.primary} size={16} weight="500" />

            {/* Counter-clockwise arrows — color by destination in reversed flow */}
            {RIGHT_ANGLES.map((a, i) => {
              const b = RIGHT_ANGLES[(i + 1) % RIGHT_ANGLES.length];
              const ac = RIGHT_ARROW_COLORS[i];
              return (
                <path key={`ra${i}`} d={arcCCW(R_CX, R_CY, LOOP_R, b, a)}
                  fill="none" stroke={ac.dark} strokeWidth={4} markerEnd={markerId(ac)} />
              );
            })}

            {rightNodes.slice(1).map((n, i) => {
              const nc = RIGHT_NODE_COLORS[i + 1];
              return (
                <g key={`rn${i}`}>
                  <circle cx={n.x} cy={n.y} r={NODE_R} fill={gradFill(nc)} stroke="white" strokeWidth={2} />
                  <SvgText x={n.x} y={n.y} text={n.label} size={14} weight="500" fill="white" />
                </g>
              );
            })}

            {/* ═══════════ SHARED USER ENGAGEMENT HUB ═══════════ */}

            <circle cx={UE_X} cy={UE_Y} r={HUB_R} fill={`url(#${GRAD_IDS.blue})`} />
            <circle cx={UE_X} cy={UE_Y} r={HUB_R} fill="none" stroke="white" strokeWidth={2.5} />
            <SvgText x={UE_X} y={UE_Y} text={"User\nEngagement"}
              size={14} weight="500" fill="white" />

            {/* ═══════════ VISION GUIDED PRODUCTS ═══════════ */}

            <rect x={VIS_X - 88} y={VIS_Y - 24} width={176} height={48} rx={14}
              fill={`url(#${GRAD_IDS.blue})`} stroke="white" strokeWidth={2} />
            <SvgText x={VIS_X} y={VIS_Y} text={"Vision Guided Products"}
              size={14} weight="500" fill="white" />

            {/* Arrow exits left side of box, curves down to UE hub */}
            <path
              d={`M${VIS_X - 88},${VIS_Y} C${VIS_X - 150},${VIS_Y + 40} ${UE_X - 40},${UE_Y - 120} ${UE_X},${UE_Y - HUB_R - 4}`}
              fill="none" stroke={PAL.blue.dark} strokeWidth={4} markerEnd="url(#a-blue)" />
          </DiagramSvg>
        </DiagramWrapper>
      </SlideContent>
    </SlideBase>
  );
};
